import { useApplications, type Application } from '@/hooks/useApplications'
import { Users, TrendingUp, CheckCircle2, Clock, Sparkles } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'

const STATUS_COLORS: Record<Application['status'], string> = {
    new: '#60a5fa',
    reviewed: '#eab308',
    shortlisted: '#22c55e',
    interviewing: '#f97316',
    offered: '#a78bfa',
    hired: '#4ade80',
    rejected: '#f87171',
}

export default function Dashboard() {
    const { apps, loading } = useApplications()

    const total = apps.length
    const newApps = apps.filter(a => a.status === 'new').length
    const aiProcessed = apps.filter(a => a.aiProcessed).length
    const avgScore = aiProcessed
        ? Math.round(apps.filter(a => a.aiScore != null).reduce((s, a) => s + (a.aiScore ?? 0), 0) / aiProcessed)
        : 0

    // By-position bar data
    const byPosition = Object.entries(
        apps.reduce((acc, a) => { acc[a.position] = (acc[a.position] ?? 0) + 1; return acc }, {} as Record<string, number>)
    ).sort((a, b) => b[1] - a[1]).slice(0, 6)

    // Recent 5
    const recent = [...apps].sort((a, b) =>
        new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
    ).slice(0, 5)

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">Dashboard</h1>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
            </div>

            {/* Stat cards */}
            <div className="stats-grid">
                {[
                    { label: 'Total Applicants', value: loading ? '—' : total, icon: <Users size={16} />, color: '#60a5fa' },
                    { label: 'New (Unreviewed)', value: loading ? '—' : newApps, icon: <Clock size={16} />, color: '#eab308' },
                    { label: 'AI Processed', value: loading ? '—' : aiProcessed, icon: <Sparkles size={16} />, color: '#a78bfa' },
                    { label: 'Avg AI Score', value: loading ? '—' : `${avgScore}`, icon: <TrendingUp size={16} />, color: '#22c55e' },
                ].map(({ label, value, icon, color }) => (
                    <div className="stat-card" key={label}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color }}>
                            {icon}
                            <span className="stat-label" style={{ color: 'var(--text-muted)', letterSpacing: 0.5 }}>{label}</span>
                        </div>
                        <div className="stat-value">{value}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Applications by position */}
                <div className="card">
                    <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>Applications by Position</h2>
                    {byPosition.length === 0 ? (
                        <div className="empty-state" style={{ padding: 40 }}>No data yet</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={byPosition.map(([name, count]) => ({ name: name.slice(0, 18), count }))}>
                                <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {byPosition.map((_, i) => <Cell key={i} fill="#6c47ff" />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Recent applications */}
                <div className="card">
                    <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Recent Applications</h2>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
                    ) : recent.length === 0 ? (
                        <div className="empty-state" style={{ padding: 40 }}>No applications yet</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {recent.map(app => (
                                <div key={app.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 12px', background: 'var(--bg)', borderRadius: 8,
                                    border: '1px solid var(--border)',
                                }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{app.name}</div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{app.position}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {app.aiScore != null && (
                                            <span className={`ai-score ${app.aiScore >= 70 ? 'ai-score-high' : app.aiScore >= 40 ? 'ai-score-mid' : 'ai-score-low'}`}>
                                                <Sparkles size={10} /> {app.aiScore}
                                            </span>
                                        )}
                                        <span className={`badge badge-${app.status}`}>{app.status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
